import { Grid } from '@customink/pigment-react';
import TextField from '@mui/material/TextField';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form/dist/types';

import { SelectState } from '@/components/TaxExemption/SelectState';
import type { BusinessAddressFormData } from '@/components/TaxExemption/types';

const requiredErrorMessage = 'Please fill out this field.';

type BusinessAddressFormProps = Pick<
  UseFormReturn<BusinessAddressFormData>,
  'register' | 'control' | 'formState' | 'watch'
>;

export function BusinessAddressForm({
  register,
  control,
  formState,
  watch,
}: BusinessAddressFormProps) {
  const { errors } = formState;
  const registerTextField = useCallback(
    (
      name: keyof BusinessAddressFormData,
      required?: boolean,
      otherOptions = {},
    ) => ({
      error: !!errors[name],
      helperText: errors[name]?.message,
      InputLabelProps: { required },
      fullWidth: true,
      ...register(name, {
        required: required ? requiredErrorMessage : false,
        ...otherOptions,
      }),
    }),
    [register, errors],
  );
  return (
    <Grid
      container
      data-testid="tax-exempt-business-address-form"
      pt={3}
      spacing={2}
    >
      <Grid item sm={12}>
        <TextField
          autoFocus
          inputProps={{ autoComplete: 'organization' }}
          label="Organization (e.g. CalTech, Sharks Soccer)"
          placeholder="CalTech"
          {...registerTextField('organizationName', true)}
        />
      </Grid>
      <Grid item sm={12}>
        <TextField
          inputProps={{ autoComplete: 'address-line1' }}
          label="Street Address"
          placeholder="123 Pine St"
          {...registerTextField('line1', true)}
        />
      </Grid>
      <Grid item sm={12}>
        <TextField
          inputProps={{ autoComplete: 'address-line2' }}
          label="Address Line 2 (Optional)"
          placeholder="Apt / Suite / Floor"
          {...registerTextField('line2')}
        />
      </Grid>
      <Grid item md={4} sm={12}>
        <TextField
          inputProps={{ autoComplete: 'address-level2' }}
          label="City"
          placeholder="City"
          {...registerTextField('city', true)}
        />
      </Grid>
      <Grid item md={4} sm={12}>
        <SelectState control={control} />
      </Grid>
      <Grid item md={4} sm={12}>
        <TextField
          inputProps={{ autoComplete: 'postal-code' }}
          label="ZIP Code"
          placeholder="12345"
          {...registerTextField('postalCode', true, {
            maxLength: 5,
            minLength: 5,
          })}
        />
      </Grid>
    </Grid>
  );
}
